import React, {Fragment, useEffect, useState} from 'react';

import {PopoverBody} from 'reactstrap';
import styled from 'styled-components';

import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {PopoverForm, PopoverHeading} from 'app/general/overlays';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import {KBTreeCreateCategoryItem} from 'app/kbs/components/KBEditPage/KBTree/KBTreeCategoryItem/KBTreeCreateCategoryItem';
import {KBNavigationHistory} from 'app/kbs/components/KBNavigationHistory';
import {gt} from 'app/utils/i18n';

import {BulkArticlesCategorySelectionList} from './KBItemList/BulkArticlesCategorySelectionList';
import {KnowledgeBaseSelectionList} from './KBItemList/KnowledgeBaseSelectionList';
import {KBSelectedArticleList} from './KBSelectedArticleList';

import {
  BaseDocumentContentType,
  KBCategorySerializedType,
  KnowledgebaseSerializedType,
} from 'sweet-types';

import type {OverlayProps} from 'app/general/overlays';

export interface INavigationHistoryType {
  parentId?: number;
  id?: number;
  type?: NavigationHistoryEnum;
  text: string;
  link: string;
  icon: string;
}

interface AddToKBItemType {
  object_id: string | number;
  document_content_type: BaseDocumentContentType;
  url: string;
}
interface Props {
  overlayProps: OverlayProps;
  onSuccess: () => Promise<any>;
  documents: Array<AddToKBItemType>;
}

export enum NavigationHistoryEnum {
  CATEGORY = 'category',
  KNOWLEDGE_BASE = 'knowledge-base',
}

const initialNavigationHistory = {text: '', link: '#', icon: 'fa-home'};

export const AddToKnowledgeBasePopover = ({
  overlayProps,
  onSuccess,
  documents,
}: Props): JSX.Element => {
  const [navigationHistory, setNavigationHistory] = useState<
    INavigationHistoryType[]
  >([initialNavigationHistory]);
  const [search, setSearch] = useState<string>('');
  const [currentKb, setCurrentKb] = useState<KnowledgebaseSerializedType>();

  const handleOneLevelup = () => {
    const nav = [...navigationHistory];
    nav.pop();
    setNavigationHistory(nav);
  };

  const handleGotoHistory = (index: number) => {
    const nav = [...navigationHistory];
    nav.splice(index + 1);
    setNavigationHistory(nav);
  };

  const setParentFolder = (parentFolder: KBCategorySerializedType) => {
    if (!search) {
      setNavigationHistory((navigationHistory) => [
        ...navigationHistory,
        {
          text: parentFolder.name,
          link: parentFolder.url,
          id: parentFolder.id,
          icon: parentFolder.icon,
        },
      ]);
    }
  };

  const isRootDirectory = navigationHistory.length === 1 && !search;

  useEffect(() => {
    if (navigationHistory.length <= 1) {
      setCurrentKb(undefined);
    }
  }, [navigationHistory]);

  useEffect(() => {
    if (currentKb != null) {
      setNavigationHistory([
        initialNavigationHistory,
        {
          text: currentKb.name,
          link: currentKb.url,
          id: currentKb.id,
          icon: 'far fa-book-open',
        },
      ]);
    }
  }, [currentKb]);

  let parent_category_id: string | number = '';
  if (navigationHistory.length > 2) {
    const currentHistory = navigationHistory[navigationHistory.length - 1];
    if (currentHistory && currentHistory.id) {
      parent_category_id = currentHistory.id;
    }
  }

  return (
    <PopoverForm overlayProps={overlayProps}>
      {({close, refreshPosition}) => {
        return (
          <Fragment>
            <PopoverBody className="m-0 p-0 ">
              <StyledScrollableContainerDiv className="d-flex flex-column">
                <KBSelectedArticleList
                  onSuccess={onSuccess}
                  documentUrls={documents.map((doc) => doc.url)}
                />
                <PopoverHeading>{gt('Add to knowledge base')}</PopoverHeading>
                <SearchInputField
                  autoFocus
                  value={search}
                  onChange={(q) => setSearch(q)}
                  placeholder={gt(`Search categories`)}
                />

                <KBNavigationHistory
                  hideNavigation={!!search || navigationHistory.length <= 1}
                  navigationHistory={navigationHistory}
                  handleGotoHistory={handleGotoHistory}
                  handleOneLevelup={handleOneLevelup}
                />

                {isRootDirectory ? (
                  <KnowledgeBaseSelectionList
                    refreshPosition={refreshPosition}
                    setCurrentKb={setCurrentKb}
                  />
                ) : (
                  <BulkArticlesCategorySelectionList
                    refreshPosition={refreshPosition}
                    setParentFolder={setParentFolder}
                    onSuccess={onSuccess}
                    documents={documents}
                    search={search}
                    kb_id={currentKb != null ? currentKb.id : undefined}
                    parent_category_id={parent_category_id}
                  />
                )}
              </StyledScrollableContainerDiv>
              {!isRootDirectory && currentKb && (
                <KBTreeCreateCategoryItem
                  kb={currentKb}
                  parentCategoryURL={
                    navigationHistory.length > 2
                      ? navigationHistory[navigationHistory.length - 1].link
                      : ''
                  }
                />
              )}
            </PopoverBody>
            <StyledPopoverBottomDIV>
              <button onClick={close} className="btn btn-link btn-sm">
                <i className="fa fa-times" /> {gt('Close')}
              </button>
            </StyledPopoverBottomDIV>
          </Fragment>
        );
      }}
    </PopoverForm>
  );
};

const StyledScrollableContainerDiv = styled.div`
  max-height: calc(100vh - 200px);
  width: 274px;
`;
